<script>
import { ref, computed, reactive, onMounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { frontendURL } from '../../../helper/URLHelper';
import SecondaryNavItem from './SecondaryNavItem.vue';
import AccountContext from './AccountContext.vue';
import { FEATURE_FLAGS } from '../../../featureFlags';
import {
  getUserPermissions,
  hasPermissions,
} from '../../../helper/permissionsHelper';
import { routesWithPermissions } from '../../../routes';
import { getFullLabelTitle } from '../../../helper/labelHelper';

export default {
  name: 'SettingsSecondarySidebar',
  components: {
    AccountContext,
    SecondaryNavItem,
  },
  props: {
    accountId: {
      type: Number,
      default: 0,
    },
    labels: {
      type: Array,
      default: () => [],
    },
    inboxes: {
      type: Array,
      default: () => [],
    },
    teams: {
      type: Array,
      default: () => [],
    },
    customViews: {
      type: Array,
      default: () => [],
    },
    menuConfig: {
      type: Object,
      default: () => {},
    },
    currentUser: {
      type: Object,
      default: () => {},
    },
    isOnClinicdeskCloud: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['addLabel', 'toggleAccounts'],
  setup(props, { emit }) {
    // Reactive state
    const openGroups = reactive({});
    const route = useRoute();
    const store = useStore();

    // Refs
    const settingsGroups = ref([
      {
        id: 'gestao',
        label: 'Gestão',
        items: [
          {
            label: 'PATIENT_MANAGEMENT',
            toStateName: 'patient_management',
            toState: '',
            hasSubMenu: false,
            key: 'patient_management',
            truncateLabel: true,
            icon: 'people-team',
          },
          {
            label: 'USER_MANAGEMENT',
            toStateName: 'user_management_list',
            toState: '',
            hasSubMenu: false,
            key: 'user_management_list',
            truncateLabel: true,
            icon: 'people-team',
          },
        ],
      },
      {
        id: 'equipe',
        label: 'Equipe',
        items: [
          {
            label: 'AGENTS',
            toStateName: 'agent_list',
            toState: '',
            hasSubMenu: false,
            key: 'agent_list',
            truncateLabel: true,
            icon: 'people',
          },
          {
            label: 'TEAMS',
            toStateName: 'teams_list',
            toState: '',
            hasSubMenu: false,
            key: 'teams_list',
            truncateLabel: true,
            icon: 'people-team',
          },
        ],
      },
      {
        id: 'comunicacao',
        label: 'Comunicação',
        items: [
          {
            label: 'INBOXES',
            toStateName: 'settings_inbox_list',
            toState: '',
            hasSubMenu: false,
            key: 'settings_inbox_list',
            truncateLabel: true,
            icon: 'mail-inbox-all',
          },
          {
            label: 'ADDRESSES',
            toStateName: 'addresses_list',
            toState: '',
            key: 'addresses_list',
            truncateLabel: true,
            icon: 'gps-icon',
          },
          {
            label: 'CUSTOM_REMINDER_MESSAGES',
            toStateName: 'custom_reminder_messages',
            toState: '',
            key: 'custom_reminder_messages',
            truncateLabel: true,
            icon: 'add-alert',
          },
        ],
      },
      {
        id: 'organizacao',
        label: 'Organização & Produtividade',
        items: [
          {
            label: 'MACROS',
            toStateName: 'macros_list',
            toState: '',
            key: 'macros_list',
            truncateLabel: true,
            icon: 'flash-settings',
          },
          {
            label: 'CUSTOM_ATTRIBUTES',
            toStateName: 'attributes_list',
            toState: '',
            key: 'attributes_list',
            truncateLabel: true,
            icon: 'code',
          },
          {
            label: 'LABELS',
            toStateName: 'labels_list',
            toState: '',
            key: 'labels_list',
            truncateLabel: true,
            icon: 'tag',
          },
          {
            label: 'CANNED_RESPONSES',
            toStateName: 'canned_list',
            toState: '',
            key: 'canned_list',
            truncateLabel: true,
            icon: 'chat-multiple',
          },
        ],
      },
      {
        id: 'automacao',
        label: 'Automação & Inteligência',
        items: [
          {
            label: 'AUTOMATION',
            toStateName: 'automation_list',
            toState: '',
            key: 'automation_list',
            truncateLabel: true,
            icon: 'automation',
          },
          {
            label: 'CUSTOM_ROLES',
            toStateName: 'ia_agent_list',
            toState: '',
            beta: true,
            key: 'ia_agent_list',
            truncateLabel: true,
            icon: 'scan-person',
          },
          {
            label: 'AGENT_BOTS',
            toStateName: 'agent_bot_list',
            toState: '',
            key: 'agent_bot_list',
            truncateLabel: true,
            icon: 'bot',
          },
        ],
      },
      {
        id: 'monitoramento',
        label: 'Monitoramento',
        items: [
          {
            label: 'AUDIT_LOGS',
            toStateName: 'auditlog_list',
            toState: '',
            key: 'auditlog_list',
            truncateLabel: true,
            icon: 'key',
          },
          {
            label: 'SLA',
            toStateName: 'sla_list',
            toState: '',
            beta: true,
            key: 'sla_list',
            truncateLabel: true,
            icon: 'document-list-clock',
          },
        ],
      },
      {
        id: 'catalogo',
        label: 'Catálogo',
        items: [
          {
            label: 'INTEGRATIONS',
            toStateName: 'integrations_list',
            toState: '',
            key: 'integrations_list',
            truncateLabel: true,
            icon: 'flash-on',
          },
          {
            label: 'PRODUCTS',
            toStateName: 'products_list',
            toState: '',
            key: 'products_list',
            truncateLabel: true,
            icon: 'product-icon',
          },
        ],
      },
    ]);

    // Computed properties
    const isSettingsMenu = computed(() => {
      return props.menuConfig?.parentNav === 'settings';
    });

    const sortedSettingsGroups = computed(() => {
      return settingsGroups.value.map(group => ({
        ...group,
        items: [...group.items].sort((a, b) => a.label.localeCompare(b.label)),
        toState:
          group.toState ||
          (group.toStateName
            ? frontendURL(
                `accounts/${props.accountId}/settings/${group.toStateName}`
              )
            : ''),
      }));
    });

    const contactCustomViews = computed(() => {
      return props.customViews.filter(view => view.filter_type === 'contact');
    });

    const myTeams = computed(() => {
      if (props.currentUser.role !== 'administrator') {
        return props.teams.filter(team => {
          const { is_member: isMember } = team;
          return isMember;
        });
      }
      return props.teams;
    });

    const isFeatureEnabledonAccount = computed(() => {
      return store.getters['accounts/isFeatureEnabledonAccount'];
    });

    const accessibleMenuItems = computed(() => {
      const menuItemsFilteredByPermissions = props.menuConfig.menuItems.filter(
        menuItem => {
          const userPermissions = getUserPermissions(
            props.currentUser,
            props.accountId
          );
          return hasPermissions(
            routesWithPermissions[menuItem.toStateName],
            userPermissions
          );
        }
      );
      return menuItemsFilteredByPermissions.filter(item => {
        if (item.showOnlyOnCloud) {
          return props.isOnClinicdeskCloud;
        }
        return true;
      });
    });

    // Methods
    const showAddLabelPopup = () => {
      emit('addLabel');
    };

    const toggleAccountModal = () => {
      emit('toggleAccounts');
    };

    const showNewLink = featureFlag => {
      return isFeatureEnabledonAccount.value(props.accountId, featureFlag);
    };

    const toggleGroup = groupId => {
      openGroups[groupId] = !openGroups[groupId];
    };

    const isGroupOpen = groupId => {
      return !!openGroups[groupId];
    };

    const isActiveRoute = routeName => {
      return route.name === routeName;
    };

    const inboxSection = computed(() => {
      return {
        icon: 'folder',
        label: 'INBOXES',
        hasSubMenu: true,
        newLink: showNewLink(FEATURE_FLAGS.INBOX_MANAGEMENT),
        newLinkTag: 'NEW_INBOX',
        key: 'inbox',
        toState: frontendURL(
          `accounts/${props.accountId}/settings/inboxes/new`
        ),
        toStateName: 'settings_inbox_new',
        newLinkRouteName: 'settings_inbox_new',
        children: props.inboxes
          .map(inbox => ({
            id: inbox.id,
            label: inbox.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${props.accountId}/inbox/${inbox.id}`
            ),
            type: inbox.channel_type,
            phoneNumber: inbox.phone_number,
            reauthorizationRequired: inbox.reauthorization_required,
          }))
          .sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
          ),
      };
    });

    const labelSection = computed(() => {
      return {
        icon: 'number-symbol',
        label: 'LABELS',
        hasSubMenu: true,
        newLink: showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        key: 'label',
        toState: frontendURL(`accounts/${props.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        dataTestid: 'sidebar-new-label-button',
        children: props.labels.map(label => ({
          id: label.id,
          label: getFullLabelTitle(label),
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${props.accountId}/label/${label.title}`
          ),
        })),
      };
    });

    const contactLabelSection = computed(() => {
      return {
        icon: 'number-symbol',
        label: 'TAGGED_WITH',
        hasSubMenu: true,
        key: 'label',
        newLink: showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        toState: frontendURL(`accounts/${props.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: props.labels.map(label => ({
          id: label.id,
          label: getFullLabelTitle(label),
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${props.accountId}/labels/${label.title}/contacts`
          ),
        })),
      };
    });

    const teamSection = computed(() => {
      return {
        icon: 'people-team',
        label: 'TEAMS',
        hasSubMenu: true,
        newLink: showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_TEAM',
        key: 'team',
        toState: frontendURL(`accounts/${props.accountId}/settings/teams/new`),
        toStateName: 'settings_teams_new',
        newLinkRouteName: 'settings_teams_new',
        children: myTeams.value.map(team => ({
          id: team.id,
          label: team.name,
          truncateLabel: true,
          toState: frontendURL(`accounts/${props.accountId}/team/${team.id}`),
        })),
      };
    });

    const foldersSection = computed(() => {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_FOLDER',
        hasSubMenu: true,
        key: 'custom_view',
        children: props.customViews
          .filter(view => view.filter_type === 'conversation')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${props.accountId}/custom_view/${view.id}`
            ),
          })),
      };
    });

    const contactSegmentsSection = computed(() => {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_SEGMENTS',
        hasSubMenu: true,
        key: 'custom_view',
        children: props.customViews
          .filter(view => view.filter_type === 'contact')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${props.accountId}/contacts/custom_view/${view.id}`
            ),
          })),
      };
    });

    const additionalSecondaryMenuItems = computed(() => {
      let conversationMenuItems = [inboxSection.value, labelSection.value];
      let contactMenuItems = [contactLabelSection.value];
      if (myTeams.value.length) {
        conversationMenuItems = [teamSection.value, ...conversationMenuItems];
      }
      if (props.customViews.length) {
        conversationMenuItems = [
          foldersSection.value,
          ...conversationMenuItems,
        ];
      }
      if (contactCustomViews.value.length) {
        contactMenuItems = [contactSegmentsSection.value, ...contactMenuItems];
      }
      return {
        conversations: conversationMenuItems,
        contacts: contactMenuItems,
      };
    });

    // Lifecycle hooks
    onMounted(() => {
      // Fecha todos os grupos por padrão
      settingsGroups.value.forEach(group => {
        openGroups[group.id] = false;
      });

      // Atualiza URLs com accountId
      settingsGroups.value.forEach(group => {
        group.items.forEach(item => {
          if (!item.toState) {
            // Mapeie os paths corretos aqui baseado nos seus arquivos .routes.js
            const pathMap = {
              patient_management: 'patient-management',
              user_management_list: 'user-management',
              agent_list: 'agents',
              teams_list: 'teams',
              settings_inbox_list: 'inboxes',
              addresses_list: 'addresses',
              custom_reminder_messages: 'custom-reminder-messages',
              macros_list: 'macros',
              attributes_list: 'custom-attributes',
              labels_list: 'labels',
              canned_list: 'canned-response',
              automation_list: 'automation',
              ia_agent_list: 'ia-agents',
              agent_bot_list: 'agent-bots',
              auditlog_list: 'audit-logs',
              sla_list: 'sla',
              integrations_list: 'integrations',
              products_list: 'products',
            };

            const path = pathMap[item.toStateName];
            if (path) {
              item.toState = frontendURL(
                `accounts/${props.accountId}/settings/${path}`
              );
            }
          }
        });
      });

      // Abrir grupo do item ativo
      nextTick(() => {
        const activeGroup = sortedSettingsGroups.value.find(group =>
          group.items.some(item => item.toStateName === route.name)
        );
        if (activeGroup) {
          openGroups[activeGroup.id] = true;
        }
      });
    });

    // Watchers
    watch(
      () => route,
      newRoute => {
        if (isSettingsMenu.value) {
          const activeGroup = sortedSettingsGroups.value.find(group =>
            group.items.some(item => item.toStateName === newRoute.name)
          );
          if (activeGroup) {
            openGroups[activeGroup.id] = true;
          }
        }
      },
      { deep: true }
    );

    // Return exposed properties
    return {
      // State
      openGroups,
      settingsGroups,

      // Computed
      isSettingsMenu,
      sortedSettingsGroups,
      accessibleMenuItems,
      additionalSecondaryMenuItems,

      // Methods
      showAddLabelPopup,
      toggleAccountModal,
      toggleGroup,
      isGroupOpen,
      isActiveRoute,
    };
  },
};
</script>

<template>
  <div
    class="flex flex-col w-48 h-full px-2 pb-8 overflow-auto text-sm bg-white border-r dark:bg-slate-900 dark:border-slate-800/50 rtl:border-r-0 rtl:border-l border-slate-50"
  >
    <AccountContext @toggle-accounts="toggleAccountModal" />

    <!-- Menu de Configurações Reestruturado -->
    <div v-if="isSettingsMenu" class="pt-2">
      <div
        v-for="group in sortedSettingsGroups"
        :key="group.id"
        class="settings-group mb-2"
      >
        <button
          class="group-header w-full flex items-center px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
          :aria-expanded="isGroupOpen(group.id)"
          @click="toggleGroup(group.id)"
        >
          <span class="flex-1 text-sm">{{ group.label }}</span>
          <fluent-icon
            icon="chevron-right"
            class="transition-transform text-xs"
            :class="{ 'rotate-90': isGroupOpen(group.id) }"
            size="16"
          />
        </button>

        <transition name="slide">
          <div v-show="isGroupOpen(group.id)" class="group-items mt-1 ml-2">
            <ul class="mb-0 ml-0 list-none">
              <SecondaryNavItem
                v-for="item in group.items"
                :key="item.key || item.toStateName"
                :menu-item="item"
              />
            </ul>
          </div>
        </transition>
      </div>
    </div>

    <!-- Menu padrão para outros casos -->
    <transition-group
      v-else
      name="menu-list"
      tag="ul"
      class="pt-2 mb-0 ml-0 list-none"
    >
      <SecondaryNavItem
        v-for="menuItem in accessibleMenuItems"
        :key="menuItem.toState"
        :menu-item="menuItem"
      />
      <SecondaryNavItem
        v-for="menuItem in additionalSecondaryMenuItems[menuConfig.parentNav]"
        :key="menuItem.key"
        :menu-item="menuItem"
        @add-label="showAddLabelPopup"
      />
    </transition-group>
  </div>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.icon-chevron-right {
  transition: transform 0.2s ease;
}

.rotate-90 {
  transform: rotate(90deg);
}
</style>
